import { z } from "zod"

const optStr = (schema: z.ZodString) =>
  z.preprocess((v) => (v === "" ? undefined : v), schema.optional())

const optNum = (schema: z.ZodNumber) =>
  z.preprocess((v) => (v === "" || v === undefined || v === null ? undefined : Number(v)), schema.optional())

export const productSchema = z.object({
  // Required
  productName: z
    .string()
    .trim()
    .min(1, "Product name is required")
    .min(2, "Product name must be at least 2 characters")
    .max(200, "Product name must not exceed 200 characters"),

  productCategory: z
    .string()
    .trim()
    .min(1, "Category is required")
    .max(100, "Category must not exceed 100 characters"),

  productSubCategory: z
    .string()
    .trim()
    .min(1, "Sub-Category is required")
    .max(100, "Sub-Category must not exceed 100 characters"),

  // Optional
  description: optStr(z.string().trim().max(1000, "Max 1000 characters")),

  productCode: optStr(
    z
      .string()
      .trim()
      .min(2, "Product code must be at least 2 characters")
      .max(50, "Product code must not exceed 50 characters")
      .regex(/^[a-zA-Z0-9_-]+$/, "Product code can only contain letters, numbers, hyphens, and underscores")
  ),

  tags: z
    .array(z.string().trim().min(1, "Tag cannot be empty").max(30, "Tag must not exceed 30 characters"))
    .max(20, "Max 20 tags")
    .optional()
    .default([]),
})

export type ProductFormValues = z.infer<typeof productSchema>

export const variantSchema = z.object({
  // Required
  sku: z
    .string()
    .trim()
    .min(1, "SKU is required")
    .min(2, "SKU must be at least 2 characters")
    .max(50, "SKU must not exceed 50 characters")
    .regex(/^[a-zA-Z0-9_-]+$/, "SKU can only contain letters, numbers, hyphens, and underscores"),

  // Optional
  name: optStr(z.string().trim().min(2, "Name must be at least 2 characters").max(200, "Name must not exceed 200 characters")),

  description: optStr(z.string().trim().max(1000, "Max 1000 characters")),

  tags: z
    .array(z.string().trim().min(1, "Tag cannot be empty").max(30, "Tag must not exceed 30 characters"))
    .max(20, "Max 20 tags")
    .optional()
    .default([]),

  hsnCode: optStr(
    z
      .string()
      .trim()
      .regex(/^[0-9]{4,8}$/, "HSN code must be 4 to 8 digits")
  ),

  price: optNum(
    z
      .number()
      .gt(0, "Price must be greater than 0")
      .lt(1_000_000, "Price must be less than 1,000,000")
      .refine((val) => Number.isFinite(val), { message: "Price must be a valid number" })
      .refine((val) => Math.round(val * 100) === val * 100, {
        message: "Price can have at most 2 decimal places",
      })
  ),

  currencyCode: optStr(
    z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}$/, "Currency code must be a 3-letter ISO code (e.g. USD, INR)")
  ),

  stockQuantity: optNum(
    z
      .number()
      .gt(0, "Stock quantity must be greater than 0")
      .lt(1_000_000, "Stock quantity must be less than 1,000,000")
      .refine((val) => Number.isFinite(val), { message: "Stock quantity must be a valid number" })
      .refine(
        (val) => {
          const parts = String(val).split(".")
          return parts.length < 2 || parts[1].length <= 2
        },
        {
          message: "Stock quantity can have at most 2 decimal places",
        }
      )
  ),

  unit: optStr(z.string().trim()),
  measureUnit: optStr(z.string().trim()),
})
  .refine(
    (data) => {
      if (data.price !== undefined && data.price !== null && !data.currencyCode) {
        return false
      }
      return true
    },
    {
      message: "currency required",
      path: ["currencyCode"],
    }
  )
  .refine(
    (data) => {
      if (data.currencyCode && (data.price === undefined || data.price === null)) {
        return false
      }
      return true
    },
    {
      message: "Price is required when currency is selected",
      path: ["price"],
    }
  )
  .refine(
    (data) => {
      if (data.stockQuantity !== undefined && data.stockQuantity !== null && !data.unit) {
        return false
      }
      return true
    },
    {
      message: "unit required",
      path: ["unit"],
    }
  )
  .refine(
    (data) => {
      if (data.unit && (data.stockQuantity === undefined || data.stockQuantity === null)) {
        return false
      }
      return true
    },
    {
      message: "Stock quantity is required when unit is selected",
      path: ["stockQuantity"],
    }
  )
  .refine(
    (data) => {
      if (data.stockQuantity !== undefined && data.stockQuantity !== null && data.unit) {
        const u = data.unit.toLowerCase().trim()
        if (u === "pcs" || u === "box" || u.includes("piece") || u.includes("box")) {
          return Number.isInteger(data.stockQuantity)
        }
      }
      return true
    },
    {
      message: "Whole numbers only for pieces or box",
      path: ["stockQuantity"],
    }
  )

export type VariantFormValues = z.infer<typeof variantSchema>